import { useState } from "react";
import {
  Feather,
  Search,
  Settings,
  Monitor,
  LogOut,
  ArrowUpRight,
  Moon,
  Sunrise,
  ClipboardList,
  BookOpen,
  Heart,
  CalendarDays,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { DASHBOARD } from "@/constants/testIds";

const quickLinks = [
  { label: "Nightly Inventory", hint: "Daily practice", icon: Moon },
  { label: "Gratitude", hint: "Daily practice", icon: Sunrise },
  { label: "Spot Check", hint: "Daily practice", icon: ClipboardList },
  { label: "Readings", hint: "Daily practice", icon: BookOpen },
  { label: "Prayers", hint: "Explore", icon: Heart },
  { label: "Meetings", hint: "Explore", icon: CalendarDays },
  { label: "The Twelve Steps", hint: "The program", icon: BookOpen },
];

const IconBtn = ({ icon: Icon, label, onClick, testId }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    data-testid={testId}
    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
  >
    <Icon className="h-[18px] w-[18px]" strokeWidth={1.7} />
  </button>
);

export const TopBar = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const results = quickLinks.filter((l) =>
    l.label.toLowerCase().includes(query.toLowerCase()),
  );
  const soon = (what) =>
    toast(what, { description: "This is a design prototype." });

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1180px] items-center justify-between px-5 sm:px-8">
        <a href="/" className="flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft/50 text-primary ring-1 ring-primary/20">
            <Feather className="h-[18px] w-[18px]" strokeWidth={1.7} />
          </span>
          <span className="font-display text-lg font-medium tracking-tight text-foreground">
            12steps
          </span>
        </a>

        <div className="flex items-center gap-1 sm:gap-1.5">
          <span className="mr-1 hidden items-center gap-1.5 rounded-full border border-border bg-card-muted px-2.5 py-1 text-xs font-medium text-muted-foreground sm:inline-flex">
            <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse-soft" />
            Synced
          </span>
          <IconBtn icon={Search} label="Search" onClick={() => setOpen(true)} testId={DASHBOARD.searchButton} />
          <IconBtn icon={Settings} label="Settings" onClick={() => soon("Settings")} />
          <IconBtn icon={Monitor} label="Display" onClick={() => soon("Display options")} />
          <IconBtn icon={LogOut} label="Sign out" onClick={() => soon("Sign out")} />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogTitle className="sr-only">Search 12steps</DialogTitle>
          <DialogDescription className="sr-only">
            Search tools, readings, and steps.
          </DialogDescription>
          <div className="flex items-center gap-2.5 border-b border-border px-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools, readings, steps…"
              className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {results.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Nothing matches “{query}”.
              </p>
            ) : (
              results.map((r) => (
                <button
                  key={r.label}
                  onClick={() => {
                    setOpen(false);
                    setQuery("");
                    soon(r.label);
                  }}
                  className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-secondary"
                >
                  <r.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  <span className="flex-1 text-sm text-foreground">{r.label}</span>
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {r.hint}
                  </span>
                  <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
